import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./index.css";
import ServiceLife39Header from "./Header";
import SubHeaderBox from "../components/SubHeaderBox";
import { ShowHide } from "../components/ShowHideProduct";
import { usePDFShowHide } from "../../../../hooks/usePDFShowHide";
import { getServiceLife39LCA, updateServiceLife39LCA } from "../../../../apis/Product/ServiceLifeFlow39Api";
import FileUploadInput from "../../../CustomHeadlessTable/components/FileUploadInput";
import { AzilisSwitch } from "../../../../components/Switch";

const ServiceLife39Components = ({ productId }) => {
  const { t } = useTranslation();
  const [show, setShow] = usePDFShowHide(false, {
    forceShowInPDF: true,
    pdfDelay: 1000,
  });

  const [formData, setFormData] = useState({
    service_life_years: null,
    source_to_service_life: "",
    reference_service_life_documentation: null,
    key_properties_fu: "",
    design_application_features: "",
    indoor_environment: "",
    outdoor_environment: "",
    standard_of_installation: "",
    use_conditions: "",
    special_energy_supply_required: "No",
    special_energy_supply_description: "",
  });

  const [fileUpload, setFileUpload] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = useCallback(() => {
    if (productId) {
      getServiceLife39LCA({
        productId,
        setData: (data) => {
          if (data) {
            setFormData({
              service_life_years: data.service_life_years || null,
              source_to_service_life: data.source_to_service_life || "",
              reference_service_life_documentation: data.reference_service_life_documentation || null,
              key_properties_fu: data.key_properties_fu || "",
              design_application_features: data.design_application_features || "",
              indoor_environment: data.indoor_environment || "",
              outdoor_environment: data.outdoor_environment || "",
              standard_of_installation: data.standard_of_installation || "",
              use_conditions: data.use_conditions || "",
              special_energy_supply_required: data.special_energy_supply_required || "No",
              special_energy_supply_description: data.special_energy_supply_description || "",
            });
            if (data.reference_service_life_documentation) {
              setFileUpload(data.reference_service_life_documentation);
            }
          }
        },
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleFileUpload = (file) => {
    setFileUpload(file);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateServiceLife39LCA({
      productId,
      service_life_years: formData.service_life_years,
      source_to_service_life: formData.source_to_service_life,
      reference_service_life_documentation: fileUpload,
      key_properties_fu: formData.key_properties_fu,
      design_application_features: formData.design_application_features,
      indoor_environment: formData.indoor_environment,
      outdoor_environment: formData.outdoor_environment,
      standard_of_installation: formData.standard_of_installation,
      use_conditions: formData.use_conditions,
      special_energy_supply_required: formData.special_energy_supply_required,
      special_energy_supply_description: formData.special_energy_supply_description,
      callback: () => {
        setHasChanges(false);
        fetchData();
      },
      use_swal: true,
    });
  };

  const isSpecialEnergyYes = formData.special_energy_supply_required === "Yes";

  return (
    <div className="service-life-39-container" id="service-life-39-id">
      <ServiceLife39Header productId={productId} show={show} />
      <ShowHide show={show} setShow={setShow} />
      {show && (
        <div className="service-life-39-content">
          <div className="service-life-39-form-grid">
            {/* Row 1 */}
            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("SERVICE_LIFE_YEARS") || "Service life in years?"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="number"
                className="service-life-input"
                placeholder={t("EG_7_YEARS") || "Eg. 7 years"}
                value={formData.service_life_years || ""}
                onChange={(e) =>
                  handleInputChange("service_life_years", e.target.value ? parseFloat(e.target.value) : null)
                }
              />
            </div>

            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("SOURCE_TO_SERVICE_LIFE") || "Source to service life eg. manufacturer information or standards?"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("WRITE_NAME_YEAR") || "Write name and year of information or standard"}
                value={formData.source_to_service_life}
                onChange={(e) => handleInputChange("source_to_service_life", e.target.value)}
              />
            </div>

            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("REFERENCE_SERVICE_LIFE_DOC") || "Reference service life, documentation"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <div className="service-life-file-upload">
                <FileUploadInput
                  value={fileUpload}
                  onChange={handleFileUpload}
                  readOnly={false}
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("KEY_PROPERTIES_FU") || "Key properties of the FU"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_UV_PROTECTION") || "eg. UV protection, compressive strength"}
                value={formData.key_properties_fu}
                onChange={(e) => handleInputChange("key_properties_fu", e.target.value)}
              />
            </div>

            <div className="service-life-field-group service-life-field-group-wide">
              <SubHeaderBox
                subTitle={t("DESIGN_APPLICATION_FEATURES") || "What are the design application features?"}
                subDescription={t("DESIGN_APPLICATION_FEATURES_SUB") || "(defined by manufacturer, technical standards and the like)"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_FLOORING") || "Eg. for flooring: Applied on clean surface, indoors, and not exposed to UV or moisture"}
                value={formData.design_application_features}
                onChange={(e) => handleInputChange("design_application_features", e.target.value)}
              />
            </div>

            {/* Row 3 */}
            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("INDOOR_ENVIRONMENT") || "Indoor environment (what indoor environment is the FU designed for)"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_STANDARD_INDOOR") || "eg. standard indoor office environment"}
                value={formData.indoor_environment}
                onChange={(e) => handleInputChange("indoor_environment", e.target.value)}
              />
            </div>

            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("OUTDOOR_ENVIRONMENT") || "Outdoor environment (what outdoor environment is the FU designed for)"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_TEMPERATE_CLIMATE") || "Eg. Temperate climate with average annual rainfall of 600 mm"}
                value={formData.outdoor_environment}
                onChange={(e) => handleInputChange("outdoor_environment", e.target.value)}
              />
            </div>

            {/* Row 4 */}
            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("STANDARD_OF_INSTALLATION") || "Standard of installation?"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_INSTALLATION_TRAINED") || "eg. installation only by trained workers and by manufacturers instruction"}
                value={formData.standard_of_installation}
                onChange={(e) => handleInputChange("standard_of_installation", e.target.value)}
              />
            </div>

            <div className="service-life-field-group">
              <SubHeaderBox
                subTitle={t("USE_CONDITIONS") || "Use conditions?"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <input
                type="text"
                className="service-life-input"
                placeholder={t("EG_SOLVENTS") || "eg. can solvents be used on the surface"}
                value={formData.use_conditions}
                onChange={(e) => handleInputChange("use_conditions", e.target.value)}
              />
            </div>

            {/* Row 5 - Special Energy Supply */}
            <div className="service-life-field-group service-life-field-group-wide">
              <SubHeaderBox
                subTitle={t("SPECIAL_ENERGY_SUPPLY") || "Special energy supply requirements during use (Y (specified)/N)"}
                isNormal={true}
                showSystemBoundary={false}
                customBGColor="rgba(25, 54, 102, 0.8)"
                customTextColor="white"
              />
              <div className={`service-life-toggle-container ${formData.special_energy_supply_required === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.special_energy_supply_required}
                  setWhich={(value) => {
                    handleInputChange("special_energy_supply_required", value);
                    if (value === "No") {
                      handleInputChange("special_energy_supply_description", "");
                    }
                  }}
                />
                <button
                  className={`service-life-describe-btn ${isSpecialEnergyYes ? "enabled" : "disabled"}`}
                  onClick={() => {
                    if (isSpecialEnergyYes) {
                      // Handle describe action if needed
                    }
                  }}
                  disabled={!isSpecialEnergyYes}
                >
                  {t("DESCRIBE") || "Describe"}
                </button>
              </div>
              {isSpecialEnergyYes && (
                <div className="service-life-describe-field">
                  <input
                    type="text"
                    className="service-life-input"
                    placeholder={t("DESCRIBE_SPECIAL_ENERGY") || "Describe special energy supply requirements..."}
                    value={formData.special_energy_supply_description}
                    onChange={(e) => handleInputChange("special_energy_supply_description", e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {hasChanges && (
            <div className="service-life-save-container">
              <button className="service-life-save-btn" onClick={handleSave}>
                {t("SAVE") || "Save"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceLife39Components;

